import { useCallback, useState } from 'react'
import { FlatList, StyleSheet, View } from 'react-native'
import { ActivityIndicator, Dialog, Divider, Portal, Text, useTheme } from 'react-native-paper'
import { useDismissOnEscape } from '@/hooks/useDismissOnEscape'
import { usePendingInvites } from '@/hooks/usePendingInvites'
import { Button } from '@/ui/Button'

interface PendingInvitesModalProps {
    visible: boolean
    onDismiss: () => void
}

export function PendingInvitesModal({ visible, onDismiss }: PendingInvitesModalProps) {
    const theme = useTheme()
    const { invites, isLoading, revokeInvite, isRevoking } = usePendingInvites()
    const [confirmingId, setConfirmingId] = useState<string | null>(null)

    useDismissOnEscape(visible, onDismiss)

    const handleRevoke = useCallback(
        async (id: string) => {
            setConfirmingId(null)
            await revokeInvite(id)
        },
        [revokeInvite]
    )

    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr)
        return date.toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        })
    }

    const renderInvite = ({ item }: { item: (typeof invites)[number] }) => {
        const isConfirming = confirmingId === item.id

        return (
            <View style={styles.row}>
                <View style={styles.info}>
                    <Text variant="bodyLarge">{item.name}</Text>
                    <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>
                        {item.email}
                    </Text>
                    <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>
                        {item.role} · Sent {formatDate(item.created ?? '')}
                    </Text>
                </View>
                <View style={styles.action}>
                    {isConfirming ? (
                        <View style={styles.confirmRow}>
                            <Button
                                title="Cancel"
                                mode="text"
                                compact
                                onPress={() => setConfirmingId(null)}
                                disabled={isRevoking}
                            />
                            <Button
                                title="Confirm"
                                mode="text"
                                compact
                                color={theme.colors.error}
                                onPress={() => handleRevoke(item.id ?? '')}
                                loading={isRevoking}
                                disabled={isRevoking}
                            />
                        </View>
                    ) : (
                        <Button
                            title="Revoke"
                            mode="text"
                            compact
                            color={theme.colors.error}
                            onPress={() => setConfirmingId(item.id ?? null)}
                        />
                    )}
                </View>
            </View>
        )
    }

    return (
        <Portal>
            <Dialog visible={visible} onDismiss={onDismiss} style={styles.dialog}>
                <Dialog.Title>Pending Invites</Dialog.Title>
                <Dialog.Content>
                    {isLoading ? (
                        <ActivityIndicator style={styles.loading} />
                    ) : invites.length === 0 ? (
                        <Text style={{ color: theme.colors.onSurfaceVariant }}>
                            No pending invitations.
                        </Text>
                    ) : (
                        <FlatList
                            data={invites}
                            keyExtractor={item => item.id ?? ''}
                            renderItem={renderInvite}
                            ItemSeparatorComponent={Divider}
                            scrollEnabled={invites.length > 5}
                            style={styles.list}
                        />
                    )}
                </Dialog.Content>
                <Dialog.Actions>
                    <Button title="Close" onPress={onDismiss} />
                </Dialog.Actions>
            </Dialog>
        </Portal>
    )
}

const styles = StyleSheet.create({
    dialog: {
        maxHeight: '80%',
    },
    list: {
        maxHeight: 400,
    },
    loading: {
        paddingVertical: 24,
    },
    row: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingVertical: 12,
    },
    info: {
        flex: 1,
        gap: 2,
    },
    action: {
        marginLeft: 8,
    },
    confirmRow: {
        flexDirection: 'row',
        gap: 4,
    },
})
